/**
 * Phase Manager
 * 
 * Manages project phases for construction sequencing.
 * Supports existing, new construction, and demolition phases.
 */

import { v4 as uuidv4 } from 'uuid';

// ==================== Types ====================

export type PhaseStatus = 'Existing' | 'New' | 'Demolished' | 'Temporary';

export interface Phase {
    id: string;
    name: string;
    sequence: number;
    description?: string;
    startDate?: Date;
    endDate?: Date;
}

export interface PhaseFilter {
    id: string;
    name: string;
    phaseId: string;  // View's phase
    displayRules: PhaseDisplayRule[];
}

export interface PhaseDisplayRule {
    phaseStatus: PhaseStatus;
    display: 'ByCategory' | 'Overridden' | 'NotDisplayed';
    overrideGraphics?: {
        lineWeight?: number;
        linePattern?: 'Solid' | 'Dashed' | 'Hidden';
        color?: string;
        transparency?: number;
    };
}

export interface ElementPhaseInfo {
    elementId: string;
    createdPhaseId: string;
    demolishedPhaseId?: string;
}

// ==================== Phase Manager ====================

export class PhaseManager {
    private phases: Map<string, Phase> = new Map();
    private phaseFilters: Map<string, PhaseFilter> = new Map();
    private elementPhases: Map<string, ElementPhaseInfo> = new Map();

    constructor() {
        this.createDefaultPhases();
        this.createDefaultFilters();
    }

    private createDefaultPhases(): void {
        const defaultPhases = [
            { name: 'Existing', sequence: 1 },
            { name: 'Demolition', sequence: 2 },
            { name: 'New Construction', sequence: 3 },
        ];

        for (const def of defaultPhases) {
            this.createPhase(def.name, def.sequence);
        }
    }

    private createDefaultFilters(): void {
        const phases = this.getAllPhases();
        const existingPhase = phases.find(p => p.name === 'Existing');
        const newPhase = phases.find(p => p.name === 'New Construction');

        if (existingPhase) {
            this.createPhaseFilter('Show All', existingPhase.id, [
                { phaseStatus: 'Existing', display: 'ByCategory' },
                { phaseStatus: 'New', display: 'ByCategory' },
                { phaseStatus: 'Demolished', display: 'ByCategory' },
            ]);
        }

        if (newPhase) {
            this.createPhaseFilter('Show New', newPhase.id, [
                { phaseStatus: 'Existing', display: 'Overridden', overrideGraphics: { transparency: 50, linePattern: 'Dashed' } },
                { phaseStatus: 'New', display: 'ByCategory' },
                { phaseStatus: 'Demolished', display: 'NotDisplayed' },
            ]);

            this.createPhaseFilter('Show Complete', newPhase.id, [
                { phaseStatus: 'Existing', display: 'ByCategory' },
                { phaseStatus: 'New', display: 'ByCategory' },
                { phaseStatus: 'Demolished', display: 'NotDisplayed' },
            ]);
        }
    }

    // ==================== Phase Operations ====================

    public createPhase(name: string, sequence?: number): Phase {
        const existingSequences = Array.from(this.phases.values()).map(p => p.sequence);
        const maxSequence = existingSequences.length > 0 ? Math.max(...existingSequences) : 0;

        const phase: Phase = {
            id: uuidv4(),
            name,
            sequence: sequence ?? maxSequence + 1
        };
        this.phases.set(phase.id, phase);
        return phase;
    }

    public getPhase(id: string): Phase | undefined {
        return this.phases.get(id);
    }

    public getPhaseByName(name: string): Phase | undefined {
        for (const phase of this.phases.values()) {
            if (phase.name === name) return phase;
        }
        return undefined;
    }

    public getAllPhases(): Phase[] {
        return Array.from(this.phases.values()).sort((a, b) => a.sequence - b.sequence);
    }

    public updatePhase(id: string, updates: Partial<Phase>): void {
        const phase = this.phases.get(id);
        if (phase) {
            Object.assign(phase, updates);
        }
    }

    public deletePhase(id: string, moveToPhaseId: string): boolean {
        const phase = this.phases.get(id);
        if (!phase) return false;

        // Reassign elements
        for (const info of this.elementPhases.values()) {
            if (info.createdPhaseId === id) {
                info.createdPhaseId = moveToPhaseId;
            }
            if (info.demolishedPhaseId === id) {
                info.demolishedPhaseId = undefined;
            }
        }

        this.phases.delete(id);
        return true;
    }

    // ==================== Phase Filter Operations ====================

    public createPhaseFilter(name: string, phaseId: string, rules: PhaseDisplayRule[]): PhaseFilter {
        const filter: PhaseFilter = {
            id: uuidv4(),
            name,
            phaseId,
            displayRules: rules
        };
        this.phaseFilters.set(filter.id, filter);
        return filter;
    }

    public getPhaseFilter(id: string): PhaseFilter | undefined {
        return this.phaseFilters.get(id);
    }

    public getAllPhaseFilters(): PhaseFilter[] {
        return Array.from(this.phaseFilters.values());
    }

    // ==================== Element Phase Assignment ====================

    public setElementPhase(elementId: string, createdPhaseId: string, demolishedPhaseId?: string): void {
        this.elementPhases.set(elementId, {
            elementId,
            createdPhaseId,
            demolishedPhaseId
        });
    }

    public getElementPhaseInfo(elementId: string): ElementPhaseInfo | undefined {
        return this.elementPhases.get(elementId);
    }

    public setElementDemolished(elementId: string, demolishedPhaseId: string): void {
        const info = this.elementPhases.get(elementId);
        if (info) {
            info.demolishedPhaseId = demolishedPhaseId;
        } else {
            // Create with default created phase
            const phases = this.getAllPhases();
            this.elementPhases.set(elementId, {
                elementId,
                createdPhaseId: phases[0]?.id || '',
                demolishedPhaseId
            });
        }
    }

    public clearElementDemolition(elementId: string): void {
        const info = this.elementPhases.get(elementId);
        if (info) {
            info.demolishedPhaseId = undefined;
        }
    }

    // ==================== Phase Status Determination ====================

    public getElementStatus(elementId: string, viewPhaseId: string): PhaseStatus {
        const info = this.elementPhases.get(elementId);
        if (!info) return 'New';  // Default for unassigned

        const viewPhase = this.phases.get(viewPhaseId);
        const createdPhase = this.phases.get(info.createdPhaseId);
        const demolishedPhase = info.demolishedPhaseId ? this.phases.get(info.demolishedPhaseId) : undefined;

        if (!viewPhase || !createdPhase) return 'New';

        // Element created after view phase
        if (createdPhase.sequence > viewPhase.sequence) {
            return 'Temporary';  // Future element
        }

        // Element demolished before or at view phase
        if (demolishedPhase && demolishedPhase.sequence <= viewPhase.sequence) {
            return 'Demolished';
        }

        // Element created in view phase
        if (createdPhase.sequence === viewPhase.sequence) {
            return 'New';
        }

        // Element created before view phase and not demolished
        return 'Existing';
    }

    public isElementVisible(elementId: string, filterId: string): boolean {
        const filter = this.phaseFilters.get(filterId);
        if (!filter) return true;

        const status = this.getElementStatus(elementId, filter.phaseId);
        const rule = filter.displayRules.find(r => r.phaseStatus === status);

        return rule?.display !== 'NotDisplayed';
    }

    public getElementGraphicsOverride(elementId: string, filterId: string): PhaseDisplayRule['overrideGraphics'] | undefined {
        const filter = this.phaseFilters.get(filterId);
        if (!filter) return undefined;

        const status = this.getElementStatus(elementId, filter.phaseId);
        const rule = filter.displayRules.find(r => r.phaseStatus === status);

        return rule?.display === 'Overridden' ? rule.overrideGraphics : undefined;
    }

    // ==================== Queries ====================

    public getElementsByPhase(phaseId: string): string[] {
        const elements: string[] = [];
        for (const [elemId, info] of this.elementPhases) {
            if (info.createdPhaseId === phaseId) {
                elements.push(elemId);
            }
        }
        return elements;
    }

    public getElementsByStatus(viewPhaseId: string, status: PhaseStatus): string[] {
        const elements: string[] = [];
        for (const elemId of this.elementPhases.keys()) {
            if (this.getElementStatus(elemId, viewPhaseId) === status) {
                elements.push(elemId);
            }
        }
        return elements;
    }

    // ==================== 4D Sequencing ====================

    public getSequenceAtTime(date: Date): Phase | undefined {
        const phases = this.getAllPhases();

        for (let i = phases.length - 1; i >= 0; i--) {
            const phase = phases[i];
            if (phase.startDate && phase.startDate <= date) {
                return phase;
            }
        }

        return phases[0];
    }

    public getVisibleElementsAtTime(date: Date): string[] {
        const phase = this.getSequenceAtTime(date);
        if (!phase) return [];

        const visible: string[] = [];
        for (const elemId of this.elementPhases.keys()) {
            const status = this.getElementStatus(elemId, phase.id);
            if (status === 'Existing' || status === 'New') {
                visible.push(elemId);
            }
        }
        return visible;
    }
}
